import { defineStore } from 'pinia';

import type { FilesGrouped } from '@/features/Files/types';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import type { ResourceContainer } from '@/types';

type FilesStoreState = ResourceContainer<
  Map<string, Record<string, FilesGrouped>>
>;

export const useFilesStore = defineStore('FilesStore', {
  state: (): FilesStoreState => ({
    data: new Map<string, Record<string, FilesGrouped>>(),
    isLoading: false,
  }),

  actions: {
    async loadFiles(hash: string, tariffId: string) {
      try {
        this.isLoading = true;
        if (!this.data.has(tariffId)) {
          const filesApi = useApi().files;
          const [, request] = filesApi.loadFiles(hash, tariffId);

          const result = await request;
          if (result === null) {
            throw new VglError(
              VGL_ERROR_CODE.GENERAL_UNEXPECTED_EMPTY_RESPONSE,
              {
                additionalErrorMessage: 'files -> loadFiles',
              },
            );
          }

          this.data.set(tariffId, result);
        }
      } catch (error) {
        throw new VglError(VGL_ERROR_CODE.FILES_LOADING_FAILED, {
          additionalErrorMessage: `Error: ${getErrorMessage(
            error,
          )} - Hash: ${hash} Tariff: ${tariffId}`,
          cause: error,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
});
